/**
 * StrategyDNACard - Bounce/Break/Fakeout Analytics
 *
 * Displays: Bounce %, Break %, Fakeout %, historical patterns
 *
 * @version 1.0.0
 */

import React, { memo } from 'react';
import { Box, Typography } from '@mui/material';
import IndicatorCard from '../IndicatorCard';

const StrategyDNACard = memo(({ strategyDNA }) => {
    const bounce = strategyDNA?.bouncePercent || strategyDNA?.bounce || 0;
    const breakout = strategyDNA?.breakPercent || strategyDNA?.break || 0;
    const fakeout = strategyDNA?.fakeoutPercent || strategyDNA?.fakeout || 0;
    const totalEvents = strategyDNA?.totalEvents || 0;

    // Determine dominant pattern
    const getDominantPattern = () => {
        if (bounce >= breakout && bounce >= fakeout) return { label: 'BOUNCE', color: '#22c55e' };
        if (breakout >= bounce && breakout >= fakeout) return { label: 'BREAK', color: '#3b82f6' };
        return { label: 'FAKEOUT', color: '#ef4444' };
    };

    const dominant = getDominantPattern();

    const summary = (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            {/* Pattern Distribution Mini */}
            <Box sx={{ display: 'flex', gap: 1.5 }}>
                <Box sx={{ textAlign: 'center' }}>
                    <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#22c55e', fontFamily: 'monospace' }}>
                        {bounce}%
                    </Typography>
                    <Typography sx={{ fontSize: '8px', color: '#71717A' }}>Bounce</Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                    <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#3b82f6', fontFamily: 'monospace' }}>
                        {breakout}%
                    </Typography>
                    <Typography sx={{ fontSize: '8px', color: '#71717A' }}>Break</Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                    <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#ef4444', fontFamily: 'monospace' }}>
                        {fakeout}%
                    </Typography>
                    <Typography sx={{ fontSize: '8px', color: '#71717A' }}>Fake</Typography>
                </Box>
            </Box>

            {/* Dominant Badge */}
            <Box
                sx={{
                    backgroundColor: `${dominant.color}20`,
                    color: dominant.color,
                    fontSize: '10px',
                    fontWeight: 700,
                    padding: '3px 8px',
                    borderRadius: '4px'
                }}
            >
                {dominant.label}
            </Box>
        </Box>
    );

    return (
        <IndicatorCard
            title="STRATEGY DNA"
            icon="🧬"
            summary={summary}
            badge={`${dominant.label}`}
            badgeColor={dominant.color}
            status={dominant.label === 'BOUNCE' ? 'bullish' : dominant.label === 'BREAK' ? 'neutral' : 'bearish'}
            defaultExpanded={false}
        >
            {/* Expanded: Detailed breakdown */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {/* Bounce Bar */}
                <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography sx={{ fontSize: '10px', color: '#22c55e', fontWeight: 600 }}>
                            Bounce
                        </Typography>
                        <Typography sx={{ fontSize: '10px', color: '#22c55e', fontFamily: 'monospace' }}>
                            {bounce}%
                        </Typography>
                    </Box>
                    <Box sx={{ height: 6, backgroundColor: 'rgba(255, 255, 255, 0.1)', borderRadius: 3 }}>
                        <Box
                            sx={{
                                width: `${bounce}%`,
                                height: '100%',
                                backgroundColor: '#22c55e',
                                borderRadius: 3,
                                transition: 'width 0.3s ease'
                            }}
                        />
                    </Box>
                </Box>

                {/* Breakout Bar */}
                <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography sx={{ fontSize: '10px', color: '#3b82f6', fontWeight: 600 }}>
                            Breakout
                        </Typography>
                        <Typography sx={{ fontSize: '10px', color: '#3b82f6', fontFamily: 'monospace' }}>
                            {breakout}%
                        </Typography>
                    </Box>
                    <Box sx={{ height: 6, backgroundColor: 'rgba(255, 255, 255, 0.1)', borderRadius: 3 }}>
                        <Box
                            sx={{
                                width: `${breakout}%`,
                                height: '100%',
                                backgroundColor: '#3b82f6',
                                borderRadius: 3,
                                transition: 'width 0.3s ease'
                            }}
                        />
                    </Box>
                </Box>

                {/* Fakeout Bar */}
                <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography sx={{ fontSize: '10px', color: '#ef4444', fontWeight: 600 }}>
                            Fakeout
                        </Typography>
                        <Typography sx={{ fontSize: '10px', color: '#ef4444', fontFamily: 'monospace' }}>
                            {fakeout}%
                        </Typography>
                    </Box>
                    <Box sx={{ height: 6, backgroundColor: 'rgba(255, 255, 255, 0.1)', borderRadius: 3 }}>
                        <Box
                            sx={{
                                width: `${fakeout}%`,
                                height: '100%',
                                backgroundColor: '#ef4444',
                                borderRadius: 3,
                                transition: 'width 0.3s ease'
                            }}
                        />
                    </Box>
                </Box>

                {/* Event Count */}
                {totalEvents > 0 && (
                    <Typography sx={{ fontSize: '9px', color: '#52525B', textAlign: 'center', mt: 0.5 }}>
                        Based on {totalEvents} historical events
                    </Typography>
                )}
            </Box>
        </IndicatorCard>
    );
});

StrategyDNACard.displayName = 'StrategyDNACard';

export default StrategyDNACard;
